import React, { useEffect, useMemo, useState } from 'react';
import { RelatedLinks, type RelatedLinkItem } from '../components/ui';
import type { IconName } from '../components/icons';
import leadSyncService, { type KampagnenArt, type KampagnenZuordnung } from '../services/leadSyncService';

/**
 * Lemlist-Kampagne -> eigene Kampagne(n). Die Lemlist-Kampagnen-ID haengt an
 * Leads, Antworten, Sequenzen, ICP-, Signal- und Firmen-Mailing-Kampagnen;
 * erst diese Zuordnung macht aus der ID einen Namen und einen Sprung.
 */

export type Zuordnung = Map<string, KampagnenZuordnung[]>;

export const KAMPAGNEN_ART: Record<KampagnenArt, { label: string; icon: IconName }> = {
  icp: { label: 'ICP-Kampagne', icon: 'target' },
  signal: { label: 'Kaufsignale', icon: 'zap' },
  sequenz: { label: 'Sequenz', icon: 'workflow' },
  mailing: { label: 'Firmen-Mailing', icon: 'mail' },
};

export function kampagnenPfad(k: Pick<KampagnenZuordnung, 'art' | 'id'>): string {
  const id = encodeURIComponent(k.id);
  switch (k.art) {
    case 'icp': return `/icp-campaigns/${id}`;
    case 'signal': return `/signal-campaigns/${id}`;
    case 'sequenz': return `/sequences?id=${id}`;
    case 'mailing': return `/public-outreach/${id}`;
    default: return '/lead-sync';
  }
}

export function zuordnungAus(liste: KampagnenZuordnung[] | null | undefined): Zuordnung {
  const map: Zuordnung = new Map();
  for (const k of Array.isArray(liste) ? liste : []) {
    if (!k.lemlistCampaignId) continue;
    map.set(k.lemlistCampaignId, [...(map.get(k.lemlistCampaignId) ?? []), k]);
  }
  return map;
}

/** Einmal je Seite laden; ohne Recht oder bei Fehler bleibt die Zuordnung leer. */
export function useKampagnenZuordnung(aktiv = true): Zuordnung {
  const [liste, setListe] = useState<KampagnenZuordnung[]>([]);
  useEffect(() => {
    if (!aktiv) return undefined;
    let lebt = true;
    Promise.resolve(leadSyncService.getKampagnenZuordnung())
      .then((daten) => { if (lebt) setListe(Array.isArray(daten) ? daten : []); })
      .catch(() => { /* Zuordnung ist Zusatz; ohne sie bleiben Namen und Sprung weg */ });
    return () => { lebt = false; };
  }, [aktiv]);
  return useMemo(() => zuordnungAus(liste), [liste]);
}

/** Bester Name: eigene Kampagne vor Lemlist-Name vor roher ID. */
export function kampagnenTitel(lemlistId: string, zuordnung: Zuordnung, lemlistName?: string | null): string {
  return zuordnung.get(lemlistId)?.[0]?.name || lemlistName || lemlistId;
}

export function kampagnenLinks(
  lemlistId: string,
  zuordnung: Zuordnung,
  opts: { leads?: boolean; omit?: KampagnenArt[] } = {},
): RelatedLinkItem[] {
  const omit = new Set(opts.omit ?? []);
  const links: RelatedLinkItem[] = (zuordnung.get(lemlistId) ?? [])
    .filter((k) => !omit.has(k.art))
    .map((k) => ({
      key: `${k.art}-${k.id}`,
      label: k.name,
      icon: KAMPAGNEN_ART[k.art].icon,
      to: kampagnenPfad(k),
      title: `${KAMPAGNEN_ART[k.art].label} „${k.name}“ öffnen`,
    }));
  if (opts.leads) {
    links.push({
      key: 'leads', label: 'Leads', icon: 'users', to: `/lead-sync?kampagne=${encodeURIComponent(lemlistId)}`,
      title: 'Alle Leads dieser Lemlist-Kampagne',
    });
  }
  return links;
}

/** Name der Kampagne plus Sprung-Chips; ohne Zuordnung nur der Name. */
export const KampagneVerweis: React.FC<{
  lemlistId?: string | null;
  name?: string | null;
  zuordnung: Zuordnung;
  leads?: boolean;
  omit?: KampagnenArt[];
  className?: string;
}> = ({ lemlistId, name, zuordnung, leads, omit, className }) => {
  if (!lemlistId) return name ? <span className={className}>{name}</span> : null;
  const items = kampagnenLinks(lemlistId, zuordnung, { leads, omit });
  if (items.length === 0) return <span className={className}>{kampagnenTitel(lemlistId, zuordnung, name)}</span>;
  return <RelatedLinks className={className} items={items} label={`Kampagne ${kampagnenTitel(lemlistId, zuordnung, name)}`} />;
};
